import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Info,
  Package,
  Plus,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useCompaniesForAssignment,
  useCreateAssignment,
  useDeleteAssignment,
  useProductsForAssignment,
  useProjectsForAssignment,
  useUserAccessibleScope,
  useUserAssignments,
} from "../hooks/useUsers";
import type {
  AssignmentEntity,
  UserAccessibleScope,
  UserAssignmentsResponse,
} from "../hooks/useUsers";
import type { UserDetail } from "../types";

const BORDER = "hsl(210,30%,85%)";

const TIER_INFO: Record<
  1 | 2 | 3,
  { label: string; bg: string; fg: string; description: string }
> = {
  1: {
    label: "Tier 1 — Full Access",
    bg: "hsl(142,55%,92%)",
    fg: "hsl(142,72%,28%)",
    description:
      "This user has full access to all records based on their role. Assignments are not required.",
  },
  2: {
    label: "Tier 2 — All Records",
    bg: "hsl(210,55%,92%)",
    fg: "hsl(210,100%,28%)",
    description: "This user can see all records in permitted modules.",
  },
  3: {
    label: "Tier 3 — Assigned Only",
    bg: "hsl(38,90%,92%)",
    fg: "hsl(35,90%,32%)",
    description:
      "This user can only see explicitly assigned records. Each assignment below is independent — removing a parent does not remove its children, and adding a child does not require its parent.",
  },
};

type FlatAssignment = {
  assignmentId: string;
  entityType: AssignmentEntity;
  id: string;
  name: string;
  code: string;
  parentLabel: string | null;
};

type Props = {
  user: UserDetail;
};

export function AssignmentsTab({ user }: Props) {
  const userId = user.id;
  const assignmentsQuery = useUserAssignments(userId);
  const createAssignment = useCreateAssignment(userId);
  const deleteAssignment = useDeleteAssignment(userId);

  const [previewOpen, setPreviewOpen] = useState(false);
  const scopeQuery = useUserAccessibleScope(userId, previewOpen);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; label: string } | null>(null);

  function flash(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  }

  const flat = useMemo(
    () => flattenAssignments(assignmentsQuery.data),
    [assignmentsQuery.data],
  );

  const tier = scopeQuery.data?.tier ?? deriveTierFromRoles(user.roles);
  const tierInfo = TIER_INFO[tier];

  function handleRemove(assignmentId: string, label: string) {
    setRemoveTarget({ id: assignmentId, label });
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    await deleteAssignment.mutateAsync(removeTarget.id);
    flash(`Removed ${removeTarget.label}`);
  }

  async function handleAdd(
    entityType: AssignmentEntity,
    entityId: string,
    label: string,
  ) {
    await createAssignment.mutateAsync({ entityType, entityId });
    flash(`Added ${label}`);
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-foreground">
          Entity Assignments
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Control record-level access. Each assignment is independent.
        </p>
      </div>

      <div
        className="flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]"
        style={{
          background: "hsl(210,55%,97%)",
          borderColor: "hsl(210,55%,80%)",
          color: "hsl(210,100%,30%)",
        }}
      >
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
        <div>
          <div>Add specific entities this user should have access to:</div>
          <ul className="mt-1 ml-3 list-disc space-y-0.5">
            <li>
              <strong>Add a Company</strong> → user sees all that company's
              products and projects
            </li>
            <li>
              <strong>Add a Product</strong> → user sees only that product and
              its projects (no other company-level data)
            </li>
            <li>
              <strong>Add a Project</strong> → user sees only that project (no
              other product/company data)
            </li>
          </ul>
          <div className="mt-1">Assignments are additive. Add as many as needed.</div>
        </div>
      </div>

      <div
        className="rounded-md border px-3 py-2 text-[12px]"
        style={{ borderColor: BORDER }}
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{ background: tierInfo.bg, color: tierInfo.fg }}
          >
            {tierInfo.label}
          </span>
        </div>
        <p className="mt-1.5 text-muted-foreground">{tierInfo.description}</p>
      </div>

      {tier !== 3 ? (
        <div
          className="rounded-md border px-3 py-3 text-[12px] text-muted-foreground"
          style={{ borderColor: BORDER, background: "hsl(210,30%,98%)" }}
        >
          Full access — assignments not required.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {flat.companies.length + flat.products.length + flat.projects.length}{" "}
              assignment
              {flat.companies.length + flat.products.length + flat.projects.length === 1
                ? ""
                : "s"}
            </div>
            <div className="flex items-center gap-3">
              {toast && (
                <span className="text-[12px]" style={{ color: "hsl(142,72%,28%)" }}>
                  {toast}
                </span>
              )}
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                Add Assignment
              </Button>
            </div>
          </div>

          <Section
            icon={<Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
            title="Companies"
            count={flat.companies.length}
            empty="No company-level assignments"
          >
            {flat.companies.map((c) => (
              <Row
                key={c.assignmentId}
                primary={c.name}
                code={c.code}
                onRemove={() => handleRemove(c.assignmentId, c.name)}
                disabled={deleteAssignment.isPending}
              />
            ))}
          </Section>

          <Section
            icon={<Package className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
            title="Products"
            count={flat.products.length}
            empty="No product-level assignments"
          >
            {flat.products.map((p) => (
              <Row
                key={p.assignmentId}
                primary={p.name}
                code={p.code}
                secondary={p.parentLabel ?? undefined}
                onRemove={() => handleRemove(p.assignmentId, p.name)}
                disabled={deleteAssignment.isPending}
              />
            ))}
          </Section>

          <Section
            icon={<FolderKanban className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
            title="Projects"
            count={flat.projects.length}
            empty="No project-level assignments"
          >
            {flat.projects.map((p) => (
              <Row
                key={p.assignmentId}
                primary={p.name}
                code={p.code}
                secondary={p.parentLabel ?? undefined}
                onRemove={() => handleRemove(p.assignmentId, p.name)}
                disabled={deleteAssignment.isPending}
              />
            ))}
          </Section>
        </>
      )}

      <div className="rounded-md border" style={{ borderColor: BORDER }}>
        <button
          type="button"
          onClick={() => setPreviewOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
        >
          <span className="text-sm font-semibold text-foreground">
            Access Preview
          </span>
          {previewOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {previewOpen && (
          <div
            className="space-y-3 border-t px-4 py-3 text-sm"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            {scopeQuery.isLoading ? (
              <div className="text-muted-foreground">
                Calculating what this user can see…
              </div>
            ) : !scopeQuery.data ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => scopeQuery.refetch()}
              >
                Calculate Access
              </Button>
            ) : (
              <PreviewBody data={scopeQuery.data} />
            )}
          </div>
        )}
      </div>

      <AddAssignmentDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        existing={flat}
        onAdd={handleAdd}
        adding={createAssignment.isPending}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Remove assignment?"
        description={
          removeTarget
            ? `Are you sure you want to remove the assignment to '${removeTarget.label}' from this user?`
            : ""
        }
        confirmLabel="Remove"
        onConfirm={confirmRemove}
      />
    </div>
  );
}

/* ──────────────────── Section + Row primitives ──────────────────── */

function Section({
  icon,
  title,
  count,
  empty,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-md border bg-white"
      style={{ borderColor: BORDER }}
    >
      <div
        className="flex items-center gap-2 border-b px-4 py-2.5"
        style={{ borderColor: "hsl(210,30%,90%)" }}
      >
        {icon}
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <span className="text-[11px] text-muted-foreground">({count})</span>
      </div>
      {count === 0 ? (
        <div className="px-4 py-3 text-[12px] italic text-muted-foreground">
          {empty}
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: "hsl(210,30%,92%)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Row({
  primary,
  code,
  secondary,
  onRemove,
  disabled,
}: {
  primary: string;
  code: string;
  secondary?: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2">
      <span
        className="inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
        style={{ background: "hsl(210,16%,93%)" }}
      >
        {code}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{primary}</div>
        {secondary && (
          <div className="truncate text-[11px] text-muted-foreground">
            {secondary}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="text-muted-foreground hover:text-[hsl(0,70%,45%)] disabled:opacity-50"
        title="Remove assignment"
        aria-label={`Remove ${primary}`}
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}

/* ─────────────────────── Add Assignment dialog ─────────────────────── */

function AddAssignmentDialog({
  open,
  onOpenChange,
  existing,
  onAdd,
  adding,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existing: ReturnType<typeof flattenAssignments>;
  onAdd: (
    entityType: AssignmentEntity,
    entityId: string,
    label: string,
  ) => Promise<void>;
  adding: boolean;
}) {
  const [type, setType] = useState<AssignmentEntity>("project");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string>("");

  // Reset internal state every time the dialog reopens
  useEffect(() => {
    if (open) {
      setType("project");
      setSearch("");
      setSelectedId("");
    }
  }, [open]);

  // Reset selection + search whenever the type changes
  useEffect(() => {
    setSelectedId("");
    setSearch("");
  }, [type]);

  const companiesQuery = useCompaniesForAssignment();
  const productsQuery = useProductsForAssignment(null);
  const projectsQuery = useProjectsForAssignment(null, null);

  const existingCompanyIds = useMemo(
    () => new Set(existing.companies.map((c) => c.id)),
    [existing.companies],
  );
  const existingProductIds = useMemo(
    () => new Set(existing.products.map((p) => p.id)),
    [existing.products],
  );
  const existingProjectIds = useMemo(
    () => new Set(existing.projects.map((p) => p.id)),
    [existing.projects],
  );

  const options = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = (text: string) =>
      term === "" || text.toLowerCase().includes(term);

    if (type === "company") {
      const data = companiesQuery.data?.data ?? [];
      return data
        .filter((c) => !existingCompanyIds.has(c.id))
        .filter((c) => matches(`${c.name} ${c.code}`))
        .map((c) => ({
          id: c.id,
          name: c.name,
          code: c.code,
          parent: null as string | null,
        }));
    }
    if (type === "product") {
      const data = productsQuery.data?.data ?? [];
      return data
        .filter((p) => !existingProductIds.has(p.id))
        .filter((p) => matches(`${p.name} ${p.code} ${p.companyName ?? ""}`))
        .map((p) => ({
          id: p.id,
          name: p.name,
          code: p.code,
          parent: p.companyName ?? null,
        }));
    }
    const data = projectsQuery.data?.data ?? [];
    return data
      .filter((p) => !existingProjectIds.has(p.id))
      .filter((p) =>
        matches(
          `${p.name} ${p.code} ${p.productName ?? ""} ${p.companyName ?? ""}`,
        ),
      )
      .map((p) => ({
        id: p.id,
        name: p.name,
        code: p.code,
        parent:
          [p.productName, p.companyName].filter(Boolean).join(" / ") || null,
      }));
  }, [
    type,
    search,
    companiesQuery.data,
    productsQuery.data,
    projectsQuery.data,
    existingCompanyIds,
    existingProductIds,
    existingProjectIds,
  ]);

  const loading =
    (type === "company" && companiesQuery.isLoading) ||
    (type === "product" && productsQuery.isLoading) ||
    (type === "project" && projectsQuery.isLoading);

  const selected = options.find((o) => o.id === selectedId);

  async function confirm() {
    if (!selected) return;
    await onAdd(type, selected.id, selected.name);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add Assignment</DialogTitle>
          <DialogDescription>
            Pick an entity to grant this user access to. Each assignment is
            independent — adding a Project does not also grant access to its
            parent Product or Company.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase text-muted-foreground">
              Entity type
            </div>
            <div className="flex gap-2">
              {(["company", "product", "project"] as AssignmentEntity[]).map(
                (t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm capitalize transition-colors ${
                      type === t
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-border text-foreground hover:bg-muted/40"
                    }`}
                  >
                    {t}
                  </button>
                ),
              )}
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase text-muted-foreground">
              Pick a {type}
            </div>
            <Input
              placeholder={`Search ${type}s…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div
              className="mt-2 max-h-[260px] overflow-y-auto rounded-md border"
              style={{ borderColor: BORDER }}
            >
              {loading ? (
                <div className="px-3 py-3 text-[12px] text-muted-foreground">
                  Loading…
                </div>
              ) : options.length === 0 ? (
                <div className="px-3 py-3 text-[12px] text-muted-foreground">
                  No matches.
                </div>
              ) : (
                <ul className="divide-y" style={{ borderColor: "hsl(210,30%,92%)" }}>
                  {options.map((o) => {
                    const active = selectedId === o.id;
                    return (
                      <li key={o.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(o.id)}
                          className={`flex w-full items-center gap-3 px-3 py-2 text-left ${
                            active ? "bg-primary/5" : "hover:bg-muted/40"
                          }`}
                        >
                          <span
                            className="inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                            style={{ background: "hsl(210,16%,93%)" }}
                          >
                            {o.code}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">
                              {o.name}
                            </div>
                            {o.parent && (
                              <div className="truncate text-[11px] text-muted-foreground">
                                under {o.parent}
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={adding}
          >
            Cancel
          </Button>
          <Button onClick={confirm} disabled={!selected || adding}>
            {adding ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────── Flatten grouped server response ───────────────────── */

function flattenAssignments(data: UserAssignmentsResponse | undefined): {
  companies: FlatAssignment[];
  products: FlatAssignment[];
  projects: FlatAssignment[];
} {
  const companies: FlatAssignment[] = [];
  const products: FlatAssignment[] = [];
  const projects: FlatAssignment[] = [];

  if (!data) return { companies, products, projects };

  for (const c of data.companies) {
    companies.push({
      assignmentId: c.assignmentId,
      entityType: "company",
      id: c.id,
      name: c.name,
      code: c.code,
      parentLabel: null,
    });
    for (const p of c.assignedProducts) {
      products.push({
        assignmentId: p.assignmentId,
        entityType: "product",
        id: p.id,
        name: p.name,
        code: p.code,
        parentLabel: p.companyName ?? c.name,
      });
      for (const prj of p.assignedProjects) {
        projects.push({
          assignmentId: prj.assignmentId,
          entityType: "project",
          id: prj.id,
          name: prj.name,
          code: prj.code,
          parentLabel: `${p.name} / ${c.name}`,
        });
      }
    }
  }

  for (const p of data.standaloneProducts) {
    products.push({
      assignmentId: p.assignmentId,
      entityType: "product",
      id: p.id,
      name: p.name,
      code: p.code,
      parentLabel: p.companyName ?? null,
    });
    for (const prj of p.assignedProjects) {
      projects.push({
        assignmentId: prj.assignmentId,
        entityType: "project",
        id: prj.id,
        name: prj.name,
        code: prj.code,
        parentLabel: p.companyName
          ? `${p.name} / ${p.companyName}`
          : p.name,
      });
    }
  }

  for (const prj of data.standaloneProjects) {
    projects.push({
      assignmentId: prj.assignmentId,
      entityType: "project",
      id: prj.id,
      name: prj.name,
      code: prj.code,
      parentLabel:
        [prj.productName, prj.companyName].filter(Boolean).join(" / ") ||
        null,
    });
  }

  return { companies, products, projects };
}

/* ─────────────────────────── Access Preview ─────────────────────────── */

function deriveTierFromRoles(
  roles: { name: string }[],
): 1 | 2 | 3 {
  const names = roles.map((r) => r.name.toLowerCase());
  if (
    names.some(
      (n) => n.includes("super admin") || n.includes("managing partner"),
    )
  )
    return 1;
  if (names.some((n) => n.includes("admin"))) return 2;
  return 3;
}

type PreviewEntity = {
  kind: "Company" | "Product" | "Project";
  id: string;
  name: string;
  code: string;
  parentLabel: string | null;
};

function PreviewBody({
  data,
}: {
  data: UserAccessibleScope;
}) {
  if (data.tier !== 3) {
    return (
      <div className="text-[12px] text-muted-foreground">
        Tier {data.tier} — this user has full access without per-entity
        assignments.
      </div>
    );
  }

  const directIds = {
    company: new Set(data.directAccessCompanyIds),
    product: new Set(data.directAccessProductIds),
    project: new Set(data.directAccessProjectIds),
  };

  const fullAccess: PreviewEntity[] = [];
  const navOnly: PreviewEntity[] = [];

  for (const c of data.accessibleCompanies) {
    const entry: PreviewEntity = {
      kind: "Company",
      id: c.id,
      name: c.name,
      code: c.code,
      parentLabel: null,
    };
    (directIds.company.has(c.id) ? fullAccess : navOnly).push(entry);
  }
  for (const p of data.accessibleProducts) {
    const entry: PreviewEntity = {
      kind: "Product",
      id: p.id,
      name: p.name,
      code: p.code,
      parentLabel: p.companyName ?? null,
    };
    (directIds.product.has(p.id) ? fullAccess : navOnly).push(entry);
  }
  for (const p of data.accessibleProjects) {
    const entry: PreviewEntity = {
      kind: "Project",
      id: p.id,
      name: p.name,
      code: p.code,
      parentLabel:
        [p.productName, p.companyName].filter(Boolean).join(" / ") || null,
    };
    // Projects have no parent-only layer; everything in accessibleProjects
    // is direct. The check is a belt-and-braces guard.
    (directIds.project.has(p.id) ? fullAccess : navOnly).push(entry);
  }

  return (
    <div className="space-y-4">
      <PreviewGroup
        title={`Full Access (${fullAccess.length})`}
        emphasis="positive"
        items={fullAccess}
        emptyText="No entities with data access yet."
      />
      <PreviewGroup
        title={`Navigation Only (${navOnly.length})`}
        emphasis="muted"
        items={navOnly}
        emptyText="No parent-only entities."
        footer="Users see these entities only as context for assigned children. No data, documents, or records from these entities are visible."
      />
      <div
        className="border-t pt-2 text-[12px] text-muted-foreground"
        style={{ borderColor: "hsl(210,30%,92%)" }}
      >
        Summary: {fullAccess.length}{" "}
        {fullAccess.length === 1 ? "entity" : "entities"} with full access,{" "}
        {navOnly.length}{" "}
        {navOnly.length === 1 ? "entity" : "entities"} visible for navigation.
      </div>
    </div>
  );
}

function PreviewGroup({
  title,
  emphasis,
  items,
  emptyText,
  footer,
}: {
  title: string;
  emphasis: "positive" | "muted";
  items: PreviewEntity[];
  emptyText: string;
  footer?: string;
}) {
  const titleColor =
    emphasis === "positive" ? "hsl(142,72%,28%)" : "hsl(215,15%,40%)";

  return (
    <div>
      <div
        className="text-[12px] font-semibold"
        style={{ color: titleColor }}
      >
        {title}
      </div>
      {items.length === 0 ? (
        <div className="mt-1 text-[11px] italic text-muted-foreground">
          {emptyText}
        </div>
      ) : (
        <div className="mt-1 space-y-0.5 pl-2 text-[12px]">
          {items.map((it) => (
            <div
              key={`${it.kind}:${it.id}`}
              className="flex items-baseline gap-1.5"
            >
              <span>•</span>
              <span className="text-foreground">
                {it.name}{" "}
                <span className="font-mono text-[10px] text-muted-foreground">
                  ({it.code})
                </span>{" "}
                <span className="text-[10px] text-muted-foreground">
                  — {it.kind}
                  {it.parentLabel ? ` under ${it.parentLabel}` : ""}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
      {footer && items.length > 0 && (
        <div className="mt-1.5 pl-2 text-[11px] italic text-muted-foreground">
          {footer}
        </div>
      )}
    </div>
  );
}
